// src/pages/dataInput/sources/CatalogSource.tsx
// ─────────────────────────────────────────────────────────────────────────────
//  Catalog source picker for Collibra DGC and Alation.
//
//  COLLIBRA HIERARCHY:
//    Community → Domain(Physical Data Dict) → System → Database → Schema → Table → Column
//
//  ALATION HIERARCHY:
//    DataSource → Schema → Table → Column
//
//  UX FLOW:
//   1. Pick target + enter creds → Test Connection
//   2. Browse the hierarchy tree.  Each node can be selected:
//      ✔ Selecting a node at any level implicitly selects ALL descendants.
//      ✔ Users can select partial sub-trees by expanding and picking at a lower level.
//   3. Selected tables are collected in a "Staged tables" list.
//   4. Entity Designer: SME groups tables into named Entities (one or more
//      tables per entity).  Default = one entity per table.
// ─────────────────────────────────────────────────────────────────────────────

import React, { useCallback, useEffect, useRef, useState } from "react";
import { cls } from "../../theme/theme";
import { catalog_action, CatalogColumn, CatalogTable } from "../../API";
import { SourcePayload, SheetMode, Relationship } from "../types";

// ── Types ────────────────────────────────────────────────────────────────────

type Target = "collibra" | "alation";

interface Creds {
  host:       string;
  username?:  string;
  password?:  string;
  api_token?: string;
}

// Hierarchy node
interface HNode {
  id:           string;
  name:         string;
  description:  string;
  level:        HLevel;
  asset_type?:  string;   // actual Collibra asset type string (System, Database, Schema, Table…)
  is_table?:    boolean;  // true = this is a table/view leaf — shows columns on click
  // children loaded lazily
  children?:    HNode[];
  loading?:     boolean;
  loaded?:      boolean;
  // leaf = table; we cache columns when user previews
  columns?:     CatalogColumn[];
  columnsLoaded?: boolean;
}

// Level names — order matters for collibra

type HLevel =
  | "communities" | "domains" | "systems" | "databases" | "schemas" | "tables"
  | "datasources";   // alation only

// Ordered hierarchy chains — used to skip empty levels automatically
const COLLIBRA_CHAIN: HLevel[] = ["communities","domains","systems","databases","schemas","tables"];
const ALATION_CHAIN:  HLevel[] = ["datasources","schemas","tables"];

// Given a current level + target, return all following levels (excluding "tables" leaf)
function levelsAfter(level: HLevel, target: Target): HLevel[] {
  const chain = target === "collibra" ? COLLIBRA_CHAIN : ALATION_CHAIN;
  const idx   = chain.indexOf(level);
  return idx === -1 ? [] : chain.slice(idx + 1) as HLevel[];
}

// Extract the items array from a FETCH_HIERARCHY_LEVEL response regardless of key name
function extractItems(data: any, requestedLevel: HLevel): any[] {
  if (!data) return [];
  // Try the exact level key first, then fall back through all known keys
  return (
    data[requestedLevel] ||
    data.domains    ||
    data.systems    ||
    data.databases  ||
    data.schemas    ||
    data.tables     ||
    data.datasources||
    data.items      ||
    []
  );
}

// Icons by Collibra asset_type string (takes priority) or by level
const ASSET_TYPE_ICON: Record<string, string> = {
  "system":               "🖥",
  "database":             "🗄",
  "schema":               "📋",
  "table":                "📊",
  "database view":        "👁",
  "data set":             "📦",
  "column":               "📌",
  "physical data dictionary": "📂",
};
const LEVEL_ICON: Record<string, string> = {
  communities: "🏛",
  domains:     "📂",
  systems:     "🖥",
  databases:   "🗄",
  schemas:     "📋",
  tables:      "📊",
  datasources: "🔌",
};
function nodeIcon(node: HNode): string {
  if (node.asset_type) {
    return ASSET_TYPE_ICON[node.asset_type.toLowerCase()] || LEVEL_ICON[node.level] || "•";
  }
  return LEVEL_ICON[node.level] || "•";
}

const LEVEL_LABEL: Record<string, string> = {
  communities: "Community",
  domains:     "Domain",
  systems:     "System",
  databases:   "Database",
  schemas:     "Schema",
  tables:      "Table",
  datasources: "Data Source",
};
// Colour chip per asset type for the badge in the tree
const ASSET_TYPE_COLOR: Record<string, { bg: string; fg: string }> = {
  "system":        { bg: "#fee2e2", fg: "#991b1b" },
  "database":      { bg: "#fef3c7", fg: "#92400e" },
  "schema":        { bg: "#ede9fe", fg: "#5b21b6" },
  "table":         { bg: "#dbeafe", fg: "#1e40af" },
  "database view": { bg: "#d1fae5", fg: "#065f46" },
  "data set":      { bg: "#fce7f3", fg: "#9d174d" },
};

// Entity defined by the SME
interface EntityDef {
  entityName:  string;
  entityLabel: string;
  description: string;
  tables:      CatalogTable[];
}

interface Props {
  initialSheetMode?: SheetMode;
  onChange:          (payload: SourcePayload | null) => void;
}

// ─────────────────────────────────────────────────────────────────────────────

export const CatalogSource: React.FC<Props> = ({
  initialSheetMode = "sub_entities",
  onChange,
}) => {
  const [target,     setTarget]    = useState<Target>("collibra");
  const [creds,      setCreds]     = useState<Creds>({ host: "", username: "", password: "" });
  const [testState,  setTestState] = useState<"idle" | "testing" | "ok" | "fail">("idle");
  const [testMsg,    setTestMsg]   = useState("");

  // ── Hierarchy tree ────────────────────────────────────────────────────────
  const [roots, setRoots] = useState<HNode[]>([]);
  const [expanded, setExpanded] = useState<Set<string>>(new Set());
  const [loading,  setLoading]  = useState<Set<string>>(new Set());

  // ── Checkbox selection ────────────────────────────────────────────────────
  // checkedIds = set of node IDs explicitly checked by the user
  // A node is "checked" if it's in this set.
  // A node is "indeterminate" if some (not all) of its descendants are checked.
  const [checkedIds, setCheckedIds] = useState<Set<string>>(new Set());

  // ── Staged tables (collected from tree selections) ────────────────────────
  const [stagedTables, setStagedTables] = useState<CatalogTable[]>([]);

  // ── Column preview (right panel) ─────────────────────────────────────────
  const [previewTable,   setPreviewTable]   = useState<HNode | null>(null);
  const [previewColumns, setPreviewColumns] = useState<CatalogColumn[]>([]);
  const [previewLoading, setPreviewLoading] = useState(false);

  // ── Entity designer ───────────────────────────────────────────────────────
  const [entities,        setEntities]        = useState<EntityDef[]>([]);
  const [designerOpen,    setDesignerOpen]    = useState(false);
  // draft new entity
  const [draftName,       setDraftName]       = useState("");
  const [draftDesc,       setDraftDesc]       = useState("");
  const [draftTableIds,   setDraftTableIds]   = useState<Set<string>>(new Set());
  const [editingIdx,      setEditingIdx]      = useState<number | null>(null);

  const credsRef = useRef(creds);
  credsRef.current = creds;

  // ── API helper ────────────────────────────────────────────────────────────
  const apiCall = useCallback(async (body: Record<string, unknown>) => {
    return catalog_action({ ...body, target, creds: credsRef.current });
  }, [target]);

  // ── Test + initial load ───────────────────────────────────────────────────
  const testConnection = async () => {
    if (!creds.host) return;
    setTestState("testing"); setTestMsg("Connecting…");
    try {
      const r = await apiCall({ action: "TEST_CONNECTION" });
      if (r?.success) {
        setTestState("ok"); setTestMsg(r.msg || "Connected");
        loadRoots();
      } else {
        setTestState("fail"); setTestMsg(r?.msg || "Connection failed");
      }
    } catch (e: any) {
      setTestState("fail"); setTestMsg(String(e?.message || e));
    }
  };

  const loadRoots = async () => {
    const rootLevel = target === "collibra" ? "communities" : "datasources";
    setLoading(s => new Set(s).add("ROOT"));
    try {
      const r = await apiCall({ action: "FETCH_COMMUNITIES" });
      if (r?.success) {
        const items: HNode[] = (r.data?.items || [])
          .map((it: any) => ({
            id: it.id, name: it.name, description: it.description || "",
            level: rootLevel as HLevel,
            asset_type: it.asset_type || undefined,
          }))
          .sort((a: HNode, b: HNode) => a.name.localeCompare(b.name));
        setRoots(items);
        setExpanded(new Set());
      }
    } finally {
      setLoading(s => { const n = new Set(s); n.delete("ROOT"); return n; });
    }
  };

  // Reset on target/creds change
  useEffect(() => {
    setTestState("idle"); setRoots([]); setExpanded(new Set());
    setStagedTables([]); setEntities([]); setPreviewTable(null);
  }, [target, creds.host]);

  // ── Expand / lazy-load children ──────────────────────────────────────────
  //
  // Uses FETCH_HIERARCHY_LEVEL which delegates to fetch_direct_children on the
  // backend. fetch_direct_children uses parentId (no type filter) so it returns
  // whatever Collibra actually has as direct children of this node — sub-systems,
  // databases, schemas, tables — exactly as modelled, regardless of depth.
  //
  // For domain nodes it uses domainId with depth=1 filtering to get top-level
  // assets only (avoiding sub-system contamination from the domain-wide query).
  //
  const expandNode = async (node: HNode) => {
    const nodeId = node.id;

    if (expanded.has(nodeId)) {
      setExpanded(s => { const n = new Set(s); n.delete(nodeId); return n; });
      return;
    }

    setExpanded(s => new Set(s).add(nodeId));

    if (node.loaded) return;

    // Tables are visual leaves — clicking them previews columns, not expands
    if (node.is_table) return;

    setLoading(s => new Set(s).add(nodeId));
    try {
      // Map the current node's level to the NEXT level in the hierarchy.
      // The backend receives both so it can choose the right Collibra API.
      const nextLevel = node.level === "communities" ? "domains"
                      : node.level === "domains"     ? "systems"
                      : node.level === "systems"     ? "databases"
                      : node.level === "databases"   ? "schemas"
                      : node.level === "schemas"     ? "tables"
                      : node.level;   // custom/unknown levels — backend decides

      const r = await apiCall({
        action:       "FETCH_HIERARCHY_LEVEL",
        level:        nextLevel,        // what we want to fetch
        parent_level: node.level,       // what the parent IS — drives domainId vs parentId
        parent_id:    nodeId,
        limit:        500,
      });

      if (!r?.success) {
        setRoots(prev => updateNode(prev, nodeId, n => ({ ...n, children: [], loaded: true })));
        return;
      }

      // Backend now returns { items: [...] } for all structural levels
      const rawItems: any[] = r.data?.items || extractItems(r.data || {}, nextLevel as HLevel);

      const children: HNode[] = rawItems
        .filter((it: any) => it.id && it.name?.trim())
        .map((it: any) => ({
          id:          String(it.id),
          name:        it.name || String(it.id),
          description: it.description || "",
          level:       it.is_table ? "tables" : nextLevel as HLevel,
          asset_type:  it.asset_type || undefined,
          is_table:    !!it.is_table,
        }))
        .sort((a, b) => a.name.localeCompare(b.name));

      setRoots(prev => updateNode(prev, nodeId, n => ({ ...n, children, loaded: true })));
    } finally {
      setLoading(s => { const n = new Set(s); n.delete(nodeId); return n; });
    }
  };

  // Recursive tree update helper
  const updateNode = (nodes: HNode[], id: string, fn: (n: HNode) => HNode): HNode[] =>
    nodes.map(n => n.id === id ? fn(n) : { ...n, children: n.children ? updateNode(n.children, id, fn) : undefined });

  // ── Checkbox helpers ─────────────────────────────────────────────────────
  // Collect all table-level nodes reachable from a given node
  const collectTableNodes = (node: HNode): HNode[] => {
    if (node.level === "tables") return [node];
    return (node.children || []).flatMap(collectTableNodes);
  };

  // Get all node IDs in the subtree
  const collectAllIds = (node: HNode): string[] => {
    return [node.id, ...(node.children || []).flatMap(collectAllIds)];
  };

  // Check state for a node: "checked" | "indeterminate" | "unchecked"
  const getCheckState = (node: HNode): "checked" | "indeterminate" | "unchecked" => {
    if (checkedIds.has(node.id)) return "checked";
    if (!node.children || node.children.length === 0) return "unchecked";
    const allIds  = collectAllIds(node).slice(1); // exclude self
    const someChecked = allIds.some(id => checkedIds.has(id));
    const allChecked  = allIds.length > 0 && allIds.every(id => checkedIds.has(id));
    if (allChecked)  return "checked";
    if (someChecked) return "indeterminate";
    return "unchecked";
  };

  // Toggle a checkbox — checks/unchecks entire subtree
  const toggleCheck = async (node: HNode) => {
    const state = getCheckState(node);
    const nextChecked = new Set(checkedIds);

    if (state === "checked" || state === "indeterminate") {
      // Uncheck: remove all ids in subtree
      collectAllIds(node).forEach(id => nextChecked.delete(id));
    } else {
      // Check: add this node + all already-loaded descendants
      collectAllIds(node).forEach(id => nextChecked.add(id));
      // If not loaded yet, we still add the parent id — stageBranchFromChecked
      // will expand and collect when the user clicks "Import selected"
    }
    setCheckedIds(nextChecked);

    // If checking a table node, auto-fetch columns and stage it
    if (node.level === "tables" && !checkedIds.has(node.id)) {
      await stageTableById(node.id, node.name, node.description);
    }
    // If unchecking a table, remove from staged
    if (node.level === "tables" && checkedIds.has(node.id)) {
      removeStagedTable(node.name);
    }
  };

  // ── Column preview ────────────────────────────────────────────────────────
  const previewTableNode = async (node: HNode) => {
    if (!node.is_table && node.level !== "tables") return;
    setPreviewTable(node);

    if (node.columnsLoaded) {
      setPreviewColumns(node.columns || []);
      return;
    }

    setPreviewLoading(true);
    try {
      const r = await apiCall({ action: "FETCH_TABLE_COLUMNS", table_id: node.id });
      if (r?.success) {
        const cols: CatalogColumn[] = r.data?.columns || [];
        setPreviewColumns(cols);
        setRoots(prev => updateNode(prev, node.id, n => ({ ...n, columns: cols, columnsLoaded: true })));
      }
    } finally {
      setPreviewLoading(false);
    }
  };

  // ── Stage a table from preview ────────────────────────────────────────────
  const stageCurrentTable = async () => {
    if (!previewTable || previewColumns.length === 0) return;
    if (stagedTables.find(t => t.table_name === previewTable.name)) return;
    if (!previewTable.is_table && previewTable.level !== "tables") return;
    const entry: CatalogTable = {
      table_name:  previewTable.name,
      description: previewTable.description,
      columns:     previewColumns,
    };
    setStagedTables(prev => [...prev, entry]);
    // Auto-create a default entity (1 entity per table) if designer not open
    if (!designerOpen) {
      setEntities(prev => [
        ...prev.filter(e => e.entityName !== sanitise(previewTable.name)),
        {
          entityName:  sanitise(previewTable.name),
          entityLabel: previewTable.name,
          description: previewTable.description || "",
          tables:      [entry],
        },
      ]);
    }
  };

  // Stage all tables under a branch node that are checked
  const stageBranch = async (node: HNode) => {
    if (node.is_table || node.level === "tables") {
      // Only stage if checked (or if called directly without checkbox system)
      if (checkedIds.size === 0 || checkedIds.has(node.id)) {
        await stageTableById(node.id, node.name, node.description);
      }
      return;
    }
    // If children already loaded in tree, recurse into them
    if (node.children && node.loaded) {
      for (const child of node.children) {
        await stageBranch(child);
      }
      return;
    }
    // Need to discover children — try each candidate level, skip empty ones
    const candidateLevels = levelsAfter(node.level, target);
    for (const tryLevel of candidateLevels) {
      try {
        const r = await apiCall({ action: "FETCH_HIERARCHY_LEVEL", level: tryLevel, parent_id: node.id, limit: 200 });
        if (!r?.success) continue;
        const raw: any[] = extractItems(r.data || {}, tryLevel);
        const validItems = raw.filter((it: any) => it.id && typeof it.name === "string" && it.name.trim().length > 0);
        if (validItems.length === 0) continue;
        const children: HNode[] = validItems.map((it: any) => ({
          id: String(it.id), name: it.name || String(it.id),
          description: it.description || "", level: tryLevel,
          asset_type: it.asset_type || undefined,
        }));
        setRoots(prev => updateNode(prev, node.id, n => ({ ...n, children, loaded: true })));
        for (const child of children) {
          await stageBranch(child);
        }
        return;  // found level, stop trying deeper
      } catch (_) { /* skip failed level */ }
    }
  };

  const stageTableById = async (tableId: string, tableName: string, tableDesc: string) => {
    if (stagedTables.find(t => t.table_name === tableName)) return;
    try {
      const r = await apiCall({ action: "FETCH_TABLE_COLUMNS", table_id: tableId });
      if (r?.success) {
        const cols: CatalogColumn[] = r.data?.columns || [];
        const entry: CatalogTable = { table_name: tableName, description: tableDesc, columns: cols };
        setStagedTables(prev => [...prev, entry]);
        setEntities(prev => [
          ...prev.filter(e => e.entityName !== sanitise(tableName)),
          { entityName: sanitise(tableName), entityLabel: tableName, description: tableDesc, tables: [entry] },
        ]);
        setRoots(prev => updateNode(prev, tableId, n => ({ ...n, columns: cols, columnsLoaded: true })));
      }
    } catch (_) { /* ignore */ }
  };

  const removeStagedTable = (name: string) => {
    setStagedTables(prev => prev.filter(t => t.table_name !== name));
    setEntities(prev =>
      prev
        .map(e => ({ ...e, tables: e.tables.filter(t => t.table_name !== name) }))
        .filter(e => e.tables.length > 0)
    );
    if (previewTable?.name === name) { setPreviewTable(null); setPreviewColumns([]); }
    // Uncheck any node with this name
    setCheckedIds(prev => {
      const next = new Set(prev);
      // Find and remove node ids matching this table name by searching roots
      const findAndRemove = (nodes: HNode[]) => {
        for (const n of nodes) {
          if (n.name === name) next.delete(n.id);
          if (n.children) findAndRemove(n.children);
        }
      };
      findAndRemove(roots);
      return next;
    });
  };

  // ── Entity designer helpers ───────────────────────────────────────────────
  const sanitise = (s: string) => s.toUpperCase().replace(/\s+/g, "_").replace(/[^A-Z0-9_]/g, "");

  const openNewEntity = () => {
    setEditingIdx(null);
    setDraftName(""); setDraftDesc(""); setDraftTableIds(new Set());
  };

  const openEditEntity = (idx: number) => {
    setEditingIdx(idx);
    const e = entities[idx];
    setDraftName(e.entityLabel);
    setDraftDesc(e.description);
    setDraftTableIds(new Set(e.tables.map(t => t.table_name)));
  };

  const saveEntity = () => {
    if (!draftName.trim() || draftTableIds.size === 0) return;
    const tables = stagedTables.filter(t => draftTableIds.has(t.table_name));
    const updated: EntityDef = {
      entityName:  sanitise(draftName),
      entityLabel: draftName.trim(),
      description: draftDesc.trim(),
      tables,
    };
    setEntities(prev => {
      const next = [...prev];
      if (editingIdx !== null) { next[editingIdx] = updated; }
      else { next.push(updated); }
      return next;
    });
    setEditingIdx(null);
    setDraftName(""); setDraftDesc(""); setDraftTableIds(new Set());
  };

  const deleteEntity = (idx: number) => {
    setEntities(prev => prev.filter((_, i) => i !== idx));
  };

  // Auto-apply one-entity-per-table when designer is toggled off
  const resetToDefaultEntities = () => {
    setEntities(stagedTables.map(t => ({
      entityName:  sanitise(t.table_name),
      entityLabel: t.table_name,
      description: t.description || "",
      tables:      [t],
    })));
  };

  // ── Emit payload to parent ────────────────────────────────────────────────
  useEffect(() => {
    if (entities.length === 0 || stagedTables.length === 0) { onChange(null); return; }

    // Flatten to a single list for the existing SourcePayload CatalogTable[] shape
    // One entity = sheetMode=sub_entities if it has >1 table, else single.
    // Multiple entities = each entity becomes one SourcePayload entry... but
    // SourcePayload only supports one catalogTables array, so we use the entities
    // structure via the metadata.entity / entityDescription fields set in Step 2.
    // We encode entity mapping in a new `entityMap` field on the payload.

    const allTables = entities.flatMap(e => e.tables);
    // Dedupe
    const seen = new Set<string>();
    const uniqueTables = allTables.filter(t => { if (seen.has(t.table_name)) return false; seen.add(t.table_name); return true; });

    const sheetMode: SheetMode = entities.length > 1 ? "separate_entities" : (entities[0]?.tables.length > 1 ? "sub_entities" : "separate_entities");

    const availableTables = uniqueTables.map(t => ({
      prefix:  sanitise(t.table_name),
      label:   t.table_name,
      columns: t.columns.map((c: any) => c.name),
    }));

    onChange({
      kind:               "catalog",
      label:              `${target} · ${entities.map(e => e.entityLabel).join(", ")}`,
      sheetMode,
      catalogTarget:      target,
      catalogSourceLabel: `${target.charAt(0).toUpperCase() + target.slice(1)}`,
      catalogTables:      uniqueTables,
      availableTables,
      relationships:      [],
      // Custom extension: entity map so Step 2 can pre-fill entity info
      entityMap:          entities.map(e => ({
        entityName:  e.entityName,
        entityLabel: e.entityLabel,
        description: e.description,
        tables:      e.tables.map(t => t.table_name),
      })),
    } as any);
  }, [entities, stagedTables, target, onChange]);

  // ── Render helpers ────────────────────────────────────────────────────────
  const rootLoading = loading.has("ROOT");
  const isLeaf = (level: HLevel) => level === "tables";

  // ─────────────────────────────────────────────────────────────────────────
  return (
    <div>
      {/* ── Connection panel ──────────────────────────────────────────────── */}
      <div className="gw-form-grid" style={{ gridTemplateColumns: "180px 1fr 1fr 1fr auto", marginBottom: 16 }}>
        <div className="gw-form-group">
          <label className="gw-label">Catalog target</label>
          <select
            className="gw-select"
            value={target}
            onChange={e => { setTarget(e.target.value as Target); setTestState("idle"); }}
          >
            <option value="collibra">Collibra DGC</option>
            <option value="alation">Alation</option>
          </select>
        </div>
        <div className="gw-form-group">
          <label className="gw-label">Host URL</label>
          <input
            className="gw-input"
            placeholder="https://your-instance"
            value={creds.host}
            onChange={e => setCreds(c => ({ ...c, host: e.target.value }))}
          />
        </div>
        <div className="gw-form-group">
          <label className="gw-label">Username</label>
          <input
            className="gw-input"
            placeholder={target === "alation" ? "username (or leave blank for token)" : "username"}
            value={creds.username || ""}
            onChange={e => setCreds(c => ({ ...c, username: e.target.value }))}
          />
        </div>
        <div className="gw-form-group">
          <label className="gw-label">{target === "alation" ? "Password / API Token" : "Password"}</label>
          <input
            className="gw-input"
            type="password"
            placeholder="••••••••"
            value={creds.password || ""}
            onChange={e => setCreds(c => ({
              ...c,
              password:  e.target.value,
              api_token: target === "alation" ? e.target.value : undefined,
            }))}
          />
        </div>
        <div className="gw-form-group" style={{ alignSelf: "end" }}>
          <button
            className={cls("gw-btn", testState === "ok" ? "gw-btn-success" : "gw-btn-primary")}
            onClick={testConnection}
            disabled={!creds.host || testState === "testing"}
          >
            {testState === "testing" ? "Testing…" : testState === "ok" ? "✓ Connected" : "Test connection"}
          </button>
        </div>
      </div>

      {testState === "fail" && (
        <div className="gw-admin-error" style={{ marginBottom: 12 }}>❌ {testMsg}</div>
      )}

      {/* ── Main layout: tree + preview + staged ─────────────────────────── */}
      {testState === "ok" && (
        <div style={{ display: "grid", gridTemplateColumns: "1fr 280px", gap: 12 }}>

          {/* LEFT: Hierarchy tree */}
          <div>
            <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: 6 }}>
              <HierarchyLabel target={target} />
              {checkedIds.size > 0 && (
                <button
                  className="gw-btn gw-btn-primary gw-btn-sm"
                  onClick={async () => {
                    // Stage all checked table nodes across the tree
                    for (const root of roots) {
                      await stageBranch(root);
                    }
                  }}
                  style={{ flexShrink: 0 }}
                >
                  Import {checkedIds.size} selected →
                </button>
              )}
            </div>
            <div style={{
              border: "1px solid #c8d4e0", borderRadius: 10, overflow: "hidden",
              background: "#fff", minHeight: 360,
            }}>
              {rootLoading && (
                <div style={{ padding: 20, color: "#445566", fontSize: 13 }}>Loading…</div>
              )}
              {!rootLoading && roots.length === 0 && (
                <div style={{ padding: 20, color: "#445566", fontSize: 13 }}>
                  No items found.{" "}
                  <span style={{ color: "#1e3a6e", cursor: "pointer", textDecoration: "underline" }} onClick={loadRoots}>
                    Reload
                  </span>
                </div>
              )}
              <div style={{ maxHeight: 480, overflowY: "auto" }}>
                {roots.map(n => (
                  <TreeNode
                    key={n.id}
                    node={n}
                    depth={0}
                    expanded={expanded}
                    loading={loading}
                    previewId={previewTable?.id}
                    stagedNames={new Set(stagedTables.map(t => t.table_name))}
                    checkedIds={checkedIds}
                    getCheckState={getCheckState}
                    onExpand={expandNode}
                    onPreview={previewTableNode}
                    onToggleCheck={toggleCheck}
                  />
                ))}
              </div>
            </div>
          </div>

          {/* RIGHT: Column preview */}
          <div>
            <div style={{ fontSize: 11, fontWeight: 700, letterSpacing: "1px",
              textTransform: "uppercase", color: "#445566", marginBottom: 8 }}>
              {previewTable ? `Columns · ${previewTable.name}` : "Column preview"}
            </div>
            <div style={{
              border: "1px solid #c8d4e0", borderRadius: 10, background: "#fff",
              minHeight: 360, maxHeight: 480, overflowY: "auto",
            }}>
              {!previewTable && (
                <div style={{ padding: 14, color: "#445566", fontSize: 12 }}>
                  Click a table in the tree to preview its columns.
                </div>
              )}
              {previewLoading && (
                <div style={{ padding: 14, color: "#445566", fontSize: 12 }}>Loading columns…</div>
              )}
              {previewTable && !previewLoading && previewColumns.length === 0 && (
                <div style={{ padding: 14, color: "#445566", fontSize: 12 }}>No columns returned.</div>
              )}
              {previewTable && !previewLoading && previewColumns.length > 0 && (
                <>
                  <div style={{
                    padding: "8px 12px", display: "flex", justifyContent: "space-between",
                    alignItems: "center", borderBottom: "1px solid #e8eef5",
                  }}>
                    <span style={{ fontSize: 11, color: "#445566" }}>{previewColumns.length} columns</span>
                    <button
                      className={cls("gw-btn gw-btn-primary gw-btn-sm")}
                      onClick={stageCurrentTable}
                      disabled={!!stagedTables.find(t => t.table_name === previewTable.name)}
                    >
                      {stagedTables.find(t => t.table_name === previewTable.name) ? "✓ Added" : "+ Add table"}
                    </button>
                  </div>
                  <div style={{ padding: "6px 8px" }}>
                    {previewColumns.map((c, i) => (
                      <div key={i} style={{
                        padding: "3px 6px", borderRadius: 4, fontSize: 11,
                        fontFamily: "'JetBrains Mono', monospace",
                        display: "flex", gap: 5, alignItems: "baseline",
                      }}>
                        <span style={{ color: "#1e3a6e", width: 14, flexShrink: 0 }}>
                          {c.is_pk ? "🔑" : "·"}
                        </span>
                        <span style={{ color: "#0f1923", fontWeight: 600 }}>{c.name}</span>
                        <span style={{ color: "#778899" }}>{c.data_type}</span>
                        {c.description && (
                          <span style={{
                            color: "#556677", fontFamily: "inherit", flex: 1,
                            overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap",
                          }}>— {c.description}</span>
                        )}
                      </div>
                    ))}
                  </div>
                </>
              )}
            </div>
          </div>
        </div>
      )}

      {/* ── Staged tables ─────────────────────────────────────────────────── */}
      {stagedTables.length > 0 && (
        <div style={{ marginTop: 16 }}>
          <div style={{ display: "flex", alignItems: "center", gap: 10, marginBottom: 8, flexWrap: "wrap" }}>
            <span className="gw-label">Staged tables ({stagedTables.length})</span>
            {checkedIds.size > 0 && stagedTables.length === 0 && (
              <button
                className="gw-btn gw-btn-primary gw-btn-sm"
                onClick={() => {
                  // Stage all checked table nodes
                  roots.forEach(r => stageBranch(r));
                }}
              >
                Import {checkedIds.size} selected →
              </button>
            )}
            <button
              className="gw-btn gw-btn-ghost gw-btn-sm"
              onClick={() => { setDesignerOpen(o => !o); if (designerOpen) resetToDefaultEntities(); }}
            >
              {designerOpen ? "↩ Reset to default" : "🎨 Design entities…"}
            </button>
          </div>
          <div style={{ display: "flex", flexWrap: "wrap", gap: 6, marginBottom: 12 }}>
            {stagedTables.map(t => (
              <span key={t.table_name} className="gw-badge gw-badge-brand" style={{ paddingRight: 4 }}>
                {t.table_name} · {t.columns.length} cols
                <span
                  style={{ marginLeft: 5, cursor: "pointer", padding: "0 4px", opacity: 0.7 }}
                  onClick={() => removeStagedTable(t.table_name)}
                >✕</span>
              </span>
            ))}
          </div>

          {/* ── Entity designer ──────────────────────────────────────────── */}
          {designerOpen ? (
            <EntityDesigner
              stagedTables={stagedTables}
              entities={entities}
              draftName={draftName}
              draftDesc={draftDesc}
              draftTableIds={draftTableIds}
              editingIdx={editingIdx}
              onDraftName={setDraftName}
              onDraftDesc={setDraftDesc}
              onDraftTableIds={setDraftTableIds}
              onSave={saveEntity}
              onEdit={openEditEntity}
              onDelete={deleteEntity}
              onNew={openNewEntity}
            />
          ) : (
            /* Default: one entity per table or grouped */
            <div>
              {stagedTables.length > 1 && (
                <>
                  <div className="gw-mode-toggle">
                    {(["sub_entities", "separate_entities"] as SheetMode[]).map(m => (
                      <button
                        key={m}
                        className={cls("gw-mode-toggle-btn",
                          (entities.length === 1 ? "sub_entities" : "separate_entities") === m && "active")}
                        onClick={() => {
                          if (m === "sub_entities") {
                            setEntities([{
                              entityName:  sanitise(stagedTables[0].table_name),
                              entityLabel: stagedTables[0].table_name,
                              description: "",
                              tables:      stagedTables,
                            }]);
                          } else {
                            resetToDefaultEntities();
                          }
                        }}
                      >
                        {m === "sub_entities"
                          ? `Group all ${stagedTables.length} tables as one entity`
                          : "One entity per table"}
                      </button>
                    ))}
                  </div>
                  <div className="gw-mode-explainer">
                    {entities.length === 1
                      ? `All ${stagedTables.length} tables will be treated as sub-entities under a single parent entity.`
                      : `Each of the ${stagedTables.length} tables becomes its own independent entity.`}
                  </div>
                </>
              )}
            </div>
          )}
        </div>
      )}
    </div>
  );
};

// ─────────────────────────────────────────────────────────────────────────────
// Tree node component (recursive)
// ─────────────────────────────────────────────────────────────────────────────

// ── Checkbox component ───────────────────────────────────────────────────────
const Checkbox: React.FC<{
  state: "checked" | "indeterminate" | "unchecked";
  onChange: (e: React.MouseEvent) => void;
}> = ({ state, onChange }) => (
  <span
    onClick={onChange}
    style={{
      display:       "inline-flex",
      alignItems:    "center",
      justifyContent:"center",
      width:          16,
      height:         16,
      borderRadius:   3,
      border:         state === "unchecked" ? "2px solid #c8d4e0" : "2px solid #1e3a6e",
      background:     state === "checked" ? "#1e3a6e" : state === "indeterminate" ? "#dde7f5" : "#fff",
      flexShrink:     0,
      cursor:         "pointer",
      transition:     "all 0.1s",
    }}
  >
    {state === "checked"      && <span style={{ color: "#fff", fontSize: 10, lineHeight: 1 }}>✓</span>}
    {state === "indeterminate" && <span style={{ color: "#1e3a6e", fontSize: 10, lineHeight: 1, fontWeight: 900 }}>−</span>}
  </span>
);

interface TreeNodeProps {
  node:          HNode;
  depth:         number;
  expanded:      Set<string>;
  loading:       Set<string>;
  previewId?:    string;
  stagedNames:   Set<string>;
  checkedIds:    Set<string>;
  getCheckState: (n: HNode) => "checked" | "indeterminate" | "unchecked";
  onExpand:      (n: HNode) => void;
  onPreview:     (n: HNode) => void;
  onToggleCheck: (n: HNode) => Promise<void>;
}

const TreeNode: React.FC<TreeNodeProps> = ({
  node, depth, expanded, loading, previewId, stagedNames,
  checkedIds, getCheckState, onExpand, onPreview, onToggleCheck,
}) => {
  const isTable     = node.is_table || node.level === "tables";
  const isOpen      = expanded.has(node.id);
  const isLoading   = loading.has(node.id);
  const isActive    = previewId === node.id;
  const isStaged    = stagedNames.has(node.name);
  const checkState  = getCheckState(node);
  const hasChildren = !node.is_table && node.level !== "tables";

  const indent = depth * 18;

  return (
    <div>
      <div
        style={{
          display:    "flex",
          alignItems: "center",
          gap:        6,
          padding:    `4px 10px 4px ${10 + indent}px`,
          background: isActive ? "#dde7f5" : checkState !== "unchecked" ? "#f0fdf4" : "transparent",
          borderLeft: isActive ? "3px solid #1e3a6e"
                    : checkState === "checked" ? "3px solid #16a34a"
                    : checkState === "indeterminate" ? "3px solid #86efac"
                    : "3px solid transparent",
          userSelect: "none",
        }}
      >
        {/* Checkbox */}
        <Checkbox
          state={checkState}
          onChange={e => { e.stopPropagation(); onToggleCheck(node); }}
        />

        {/* Expand arrow (non-table nodes) */}
        {hasChildren ? (
          <span
            style={{ width: 14, fontSize: 10, color: "#778899", flexShrink: 0, cursor: "pointer" }}
            onClick={() => onExpand(node)}
          >
            {isLoading ? "⋯" : isOpen ? "▾" : "▸"}
          </span>
        ) : (
          <span style={{ width: 14 }} />
        )}

        {/* Icon */}
        <span style={{ fontSize: 12, flexShrink: 0 }}>{nodeIcon(node)}</span>

        {/* Name — click to expand (non-table) or preview (table) */}
        <span
          style={{
            fontSize: 13, color: "#0f1923", flex: 1,
            overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap",
            cursor: "pointer",
          }}
          onClick={() => isTable ? onPreview(node) : onExpand(node)}
        >
          {node.name}
        </span>

        {/* Asset-type badge */}
        {node.asset_type && (() => {
          const col = ASSET_TYPE_COLOR[node.asset_type.toLowerCase()];
          return (
            <span style={{
              fontSize: 9, borderRadius: 3, padding: "1px 5px", flexShrink: 0,
              fontWeight: 700, letterSpacing: "0.5px", textTransform: "uppercase",
              background: col?.bg || "#e5e7eb", color: col?.fg || "#374151",
            }}>
              {node.asset_type}
            </span>
          );
        })()}

        {/* Staged tick (table only) */}
        {isStaged && (
          <span style={{ fontSize: 10, background: "#dcfce7", color: "#16a34a",
            borderRadius: 4, padding: "1px 5px", fontWeight: 700, flexShrink: 0 }}>
            ✓
          </span>
        )}
      </div>

      {/* Children */}
      {isOpen && !isLoading && node.children && node.children.map(child => (
        <TreeNode
          key={child.id}
          node={child}
          depth={depth + 1}
          expanded={expanded}
          loading={loading}
          previewId={previewId}
          stagedNames={stagedNames}
          checkedIds={checkedIds}
          getCheckState={getCheckState}
          onExpand={onExpand}
          onPreview={onPreview}
          onToggleCheck={onToggleCheck}
        />
      ))}
      {isOpen && !isLoading && node.loaded && (!node.children || node.children.length === 0) && (
        <div style={{ fontSize: 12, color: "#778899", padding: `3px 10px 3px ${10 + indent + 36}px` }}>
          Empty
        </div>
      )}
    </div>
  );
};

// ─────────────────────────────────────────────────────────────────────────────
// Hierarchy label (breadcrumb-style legend)
// ─────────────────────────────────────────────────────────────────────────────

const HierarchyLabel: React.FC<{ target: Target }> = ({ target }) => {
  const levels = target === "collibra" ? COLLIBRA_CHAIN : ALATION_CHAIN;
  return (
    <div style={{ display: "flex", alignItems: "center", gap: 4, marginBottom: 8,
      fontSize: 11, color: "#556677", flexWrap: "wrap" }}>
      <span style={{ fontWeight: 600, marginRight: 4 }}>Browse:</span>
      {levels.map((l, i) => (
        <React.Fragment key={l}>
          {i > 0 && <span style={{ opacity: 0.4 }}>›</span>}
          <span style={{ background: "#eef2f8", borderRadius: 4, padding: "1px 6px" }}>
            {LEVEL_ICON[l]} {LEVEL_LABEL[l]}
          </span>
        </React.Fragment>
      ))}
      <span style={{ marginLeft: 6, opacity: 0.5, fontStyle: "italic" }}>
        (intermediate levels auto-skipped if empty)
      </span>
    </div>
  );
};

// ─────────────────────────────────────────────────────────────────────────────
// Entity designer panel
// ─────────────────────────────────────────────────────────────────────────────

interface EntityDesignerProps {
  stagedTables:  CatalogTable[];
  entities:      EntityDef[];
  draftName:     string;
  draftDesc:     string;
  draftTableIds: Set<string>;
  editingIdx:    number | null;
  onDraftName:   (v: string) => void;
  onDraftDesc:   (v: string) => void;
  onDraftTableIds: (s: Set<string>) => void;
  onSave:        () => void;
  onEdit:        (i: number) => void;
  onDelete:      (i: number) => void;
  onNew:         () => void;
}

const EntityDesigner: React.FC<EntityDesignerProps> = ({
  stagedTables, entities, draftName, draftDesc, draftTableIds,
  editingIdx, onDraftName, onDraftDesc, onDraftTableIds, onSave, onEdit, onDelete, onNew,
}) => {
  const toggleDraftTable = (name: string) => {
    const next = new Set(draftTableIds);
    if (next.has(name)) next.delete(name); else next.add(name);
    onDraftTableIds(next);
  };

  const assignedTables = new Set(entities.flatMap(e => e.tables.map(t => t.table_name)));
  const unassigned     = stagedTables.filter(t => !assignedTables.has(t.table_name));

  return (
    <div style={{
      border: "1px solid #c8d4e0", borderRadius: 10, overflow: "hidden",
      background: "#f8fafc", padding: 14, marginTop: 8,
    }}>
      <div style={{ fontWeight: 700, fontSize: 13, color: "#0f1923", marginBottom: 10 }}>
        🎨 Entity designer
      </div>

      {/* Defined entities */}
      <div style={{ display: "flex", flexWrap: "wrap", gap: 8, marginBottom: 14 }}>
        {entities.map((e, i) => (
          <div key={e.entityName} style={{
            background: "#fff", border: "1px solid #c8d4e0", borderRadius: 8,
            padding: "8px 12px", minWidth: 180, maxWidth: 260,
          }}>
            <div style={{ fontWeight: 700, fontSize: 13, color: "#1e3a6e", marginBottom: 2 }}>
              {e.entityLabel}
            </div>
            {e.description && (
              <div style={{ fontSize: 11, color: "#556677", marginBottom: 4, fontStyle: "italic" }}>
                {e.description}
              </div>
            )}
            <div style={{ display: "flex", flexWrap: "wrap", gap: 4, marginBottom: 6 }}>
              {e.tables.map(t => (
                <span key={t.table_name} style={{
                  fontSize: 10, background: "#dde7f5", color: "#1e3a6e",
                  borderRadius: 4, padding: "1px 6px", fontFamily: "'JetBrains Mono', monospace",
                }}>
                  {t.table_name}
                </span>
              ))}
            </div>
            <div style={{ display: "flex", gap: 6 }}>
              <button className="gw-btn gw-btn-ghost gw-btn-sm" onClick={() => onEdit(i)}>Edit</button>
              <button className="gw-btn gw-btn-ghost gw-btn-sm" onClick={() => onDelete(i)}>✕</button>
            </div>
          </div>
        ))}
        <div
          style={{
            border: "2px dashed #c8d4e0", borderRadius: 8, padding: "8px 14px",
            color: "#778899", fontSize: 12, cursor: "pointer", display: "flex",
            alignItems: "center", gap: 6,
          }}
          onClick={onNew}
        >
          + New entity
        </div>
      </div>

      {/* Unassigned tables warning */}
      {unassigned.length > 0 && (
        <div style={{ fontSize: 11.5, color: "#b45309", marginBottom: 10,
          background: "#fef9c3", borderRadius: 6, padding: "6px 10px" }}>
          ⚠ {unassigned.length} table(s) not assigned to any entity:{" "}
          {unassigned.map(t => t.table_name).join(", ")}
        </div>
      )}

      {/* Draft editor */}
      <div style={{
        background: "#fff", border: "1px solid #c8d4e0", borderRadius: 8, padding: 12,
      }}>
        <div style={{ fontWeight: 700, fontSize: 12, color: "#445566", marginBottom: 8 }}>
          {editingIdx !== null ? `Editing: ${entities[editingIdx]?.entityLabel}` : "New entity"}
        </div>
        <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 8, marginBottom: 8 }}>
          <div className="gw-form-group" style={{ margin: 0 }}>
            <label className="gw-label">Entity name</label>
            <input
              className="gw-input"
              placeholder="e.g. Customer Account"
              value={draftName}
              onChange={e => onDraftName(e.target.value)}
            />
          </div>
          <div className="gw-form-group" style={{ margin: 0 }}>
            <label className="gw-label">Description (optional)</label>
            <input
              className="gw-input"
              placeholder="Brief description"
              value={draftDesc}
              onChange={e => onDraftDesc(e.target.value)}
            />
          </div>
        </div>
        <div className="gw-label" style={{ marginBottom: 6 }}>Tables in this entity</div>
        <div style={{ display: "flex", flexWrap: "wrap", gap: 6, marginBottom: 10 }}>
          {stagedTables.map(t => {
            const selected = draftTableIds.has(t.table_name);
            return (
              <button
                key={t.table_name}
                type="button"
                onClick={() => toggleDraftTable(t.table_name)}
                style={{
                  border: selected ? "2px solid #1e3a6e" : "1px solid #c8d4e0",
                  background: selected ? "#dde7f5" : "#fff",
                  borderRadius: 6, padding: "4px 10px",
                  fontFamily: "'JetBrains Mono', monospace",
                  fontSize: 11, cursor: "pointer",
                  color: selected ? "#1e3a6e" : "#445566",
                  fontWeight: selected ? 700 : 400,
                }}
              >
                {selected ? "✓ " : ""}{t.table_name} ({t.columns.length} cols)
              </button>
            );
          })}
        </div>
        <button
          className="gw-btn gw-btn-primary gw-btn-sm"
          onClick={onSave}
          disabled={!draftName.trim() || draftTableIds.size === 0}
        >
          {editingIdx !== null ? "Update entity" : "Add entity"}
        </button>
      </div>
    </div>
  );
};

export default CatalogSource;